import React from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';
import AnimatedSection from '../../components/common/AnimatedSection';

// 样式组件
const PatentsContainer = styled('div', {
  padding: '0 20px',
});

const PatentsList = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '25px',
  margin: '40px 0',
});

const PatentCard = styled('div', {
  background: '$bgSecondary',
  borderRadius: '15px',
  padding: '30px',
  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.05)',
  transition: 'transform 0.3s ease',
  
  '&:hover': {
    transform: 'translateY(-3px)',
  },
  
  '& .patent-header': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
    
    '@media (max-width: 600px)': {
      flexDirection: 'column',
    }
  },
  
  '& .patent-title': {
    fontSize: '20px',
    fontWeight: '600',
    color: '$headingColor',
  },
  
  '& .patent-id': {
    padding: '5px 12px',
    background: '#F0F4FF',
    borderRadius: '20px',
    color: '#3563E9',
    fontSize: '14px',
    fontWeight: '500',
    
    '@media (max-width: 600px)': {
      marginTop: '10px',
    }
  },
  
  '& .patent-description': {
    fontSize: '16px',
    color: '$textSecondary',
    lineHeight: '1.6',
    marginBottom: '20px',
  },
  
  '& .patent-meta': {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px',
    
    '& .meta-item': {
      '& .label': {
        fontSize: '14px',
        color: '$textSecondary',
        marginBottom: '5px',
      },
      
      '& .value': {
        fontSize: '16px',
        fontWeight: '500',
        color: '$headingColor',
      }
    }
  }
});

const AdvantagesSection = styled('div', {
  background: 'linear-gradient(145deg, #F0F4FF 0%, #E5ECFF 100%)',
  borderRadius: '15px',
  padding: '40px',
  margin: '40px 0',
});

const AdvantagesList = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: '25px',
  marginTop: '30px',
});

const AdvantageItem = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  
  '& .advantage-icon': {
    fontSize: '32px',
    marginBottom: '15px',
  },
  
  '& .advantage-title': {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '10px',
    color: '$headingColor',
  },
  
  '& .advantage-description': {
    fontSize: '15px',
    color: '$textSecondary',
    lineHeight: '1.6',
  }
});

// 专利技术展示页面组件
const AiPatents: React.FC = () => {
  const { t } = useTranslation();
  
  // 示例专利数据
  const patents = [
    {
      title: t('ai.patents.patent1.title'),
      id: t('ai.patents.patent1.id'),
      description: t('ai.patents.patent1.description'),
      issuedDate: t('ai.patents.patent1.issuedDate'),
      inventors: t('ai.patents.patent1.inventors'),
      category: t('ai.patents.patent1.category')
    },
    {
      title: t('ai.patents.patent2.title'),
      id: t('ai.patents.patent2.id'),
      description: t('ai.patents.patent2.description'),
      issuedDate: t('ai.patents.patent2.issuedDate'),
      inventors: t('ai.patents.patent2.inventors'),
      category: t('ai.patents.patent2.category')
    },
    {
      title: t('ai.patents.patent3.title'),
      id: t('ai.patents.patent3.id'),
      description: t('ai.patents.patent3.description'),
      issuedDate: t('ai.patents.patent3.issuedDate'),
      inventors: t('ai.patents.patent3.inventors'),
      category: t('ai.patents.patent3.category')
    },
    {
      title: t('ai.patents.patent4.title'),
      id: t('ai.patents.patent4.id'),
      description: t('ai.patents.patent4.description'),
      issuedDate: t('ai.patents.patent4.issuedDate'),
      inventors: t('ai.patents.patent4.inventors'),
      category: t('ai.patents.patent4.category')
    }
  ];
  
  // 示例技术优势数据
  const advantages = [
    {
      icon: '⚡',
      title: t('ai.patents.advantage1.title'),
      description: t('ai.patents.advantage1.description')
    },
    {
      icon: '🔒',
      title: t('ai.patents.advantage2.title'),
      description: t('ai.patents.advantage2.description')
    },
    {
      icon: '🌍',
      title: t('ai.patents.advantage3.title'),
      description: t('ai.patents.advantage3.description')
    },
    {
      icon: '🔄',
      title: t('ai.patents.advantage4.title'),
      description: t('ai.patents.advantage4.description')
    }
  ];
  
  return (
    <PatentsContainer>
      <AnimatedSection direction="up" delay={0.2}>
        <h2 className="section-title">{t('ai.patents.title')}</h2>
        <p className="section-subtitle">{t('ai.patents.subtitle')}</p>
      </AnimatedSection>
      
      <AnimatedSection direction="up" delay={0.3}>
        <PatentsList>
          {patents.map((patent, index) => (
            <PatentCard key={index}>
              <div className="patent-header">
                <div className="patent-title">{patent.title}</div>
                <div className="patent-id">{patent.id}</div>
              </div>
              <div className="patent-description">{patent.description}</div>
              <div className="patent-meta">
                <div className="meta-item">
                  <div className="label">{t('ai.patents.issuedDateLabel')}</div>
                  <div className="value">{patent.issuedDate}</div>
                </div>
                <div className="meta-item">
                  <div className="label">{t('ai.patents.inventorsLabel')}</div>
                  <div className="value">{patent.inventors}</div>
                </div>
                <div className="meta-item">
                  <div className="label">{t('ai.patents.categoryLabel')}</div>
                  <div className="value">{patent.category}</div>
                </div>
              </div>
            </PatentCard>
          ))}
        </PatentsList>
      </AnimatedSection>
      
      <AnimatedSection direction="up" delay={0.4}>
        <h3 className="section-subtitle">{t('ai.patents.advantagesTitle')}</h3>
        <AdvantagesSection>
          <p className="section-description">{t('ai.patents.advantagesDescription')}</p>
          <AdvantagesList>
            {advantages.map((advantage, index) => (
              <AdvantageItem key={index}>
                <div className="advantage-icon">{advantage.icon}</div>
                <div className="advantage-title">{advantage.title}</div>
                <div className="advantage-description">{advantage.description}</div>
              </AdvantageItem>
            ))}
          </AdvantagesList>
        </AdvantagesSection>
      </AnimatedSection>
    </PatentsContainer>
  );
};

export default AiPatents; 